import React from 'react';
import { RocketLaunchIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ToolCallout from '../../../components/playbooks/ToolCallout';

const FeedbackCultureStage2 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 2: Recognising Each Other's Needs"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading
      >
        <ExpandableCallout
          type="info"
          title="What do I need to DO in this stage?"
          defaultOpen={true}
        >
          <div className="space-y-4">
            <p className="text-gray-300">You need to do 2 things:</p>
            <ol className="space-y-2 list-decimal list-inside">
              <li className="text-gray-300">Get everyone on the team to choose a feedback style and share that with everyone else on the team</li>
              <li className="text-gray-300">Make that info easy to find (e.g. on team directory/embedded in personal readme/userguide2me etc…)</li>
            </ol>
            <p className="text-gray-300 italic">Here's what we mean...</p>
          </div>
        </ExpandableCallout>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Get folks on the team to choose a feedback style and share that with everyone else</h2>
          <div className="space-y-8">
            {/* Step 1 */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Step 1: Prepare a Miro board</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-300">
                <li>A card for each of the feedback styles you introduced in Stage 1.</li>
                <li>
                  An avatar of every person on the team and some space to add sticky notes that describe:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>How you like to receive feedback (SBI/Glows n Grows etc...)</li>
                    <li>When you like to receive feedback</li>
                    <li>What channel (face to face, written, etc..)</li>
                    <li>Something you are working on right now (a skill e.g. clearer written comms)</li>
                  </ul>
                </li>
              </ul>
            </div>
            {/* Step 2 */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Step 2: Record a short Loom explaining the goals</h3>
              <ul className="list-disc pl-6 space-y-1 text-gray-300">
                <li>Refresh on different feedback styles</li>
                <li>Choose your preference and record it next to your avatar on the Miro board</li>
              </ul>
            </div>
            {/* Step 3 */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Step 3: Share and set a timeline</h3>
              <p className="text-gray-300 mb-4">
                Share the Loom and Miro board link with folks (email/slack – whatever works for your org) and set an async timeline of 2-3 days for completion, making it clear that you will collect the output and share it in the next step.
              </p>
            </div>
          </div>
          {/* Pro Tip and Additional Opportunity */}
          <ExpandableCallout
            type="tip"
            title="Pro Tip"
          >
            <p className="text-gray-300">
              You can run this as a synchronous session, but as it's mostly reflective, I think async is more suitable usually.
            </p>
          </ExpandableCallout>
          <ExpandableCallout
            type="tip"
            title="Additional Opportunity"
          >
            <p className="text-gray-300 mb-4">
              You can also use this session as an opportunity to go beyond 'feedback preferences' and expand into:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                personal readme / user manual workshop
              </li>
            </ul>
            <div className="mt-4">
              <ToolCallout
                tool="miro"
                label="Use this Miro Template by Gaby Turner"
                href="https://miro.com/miroverse/user-guide-me-template/"
                description="A collaborative Miro board for sharing team feedback styles and preferences."
              />
            </div>
          </ExpandableCallout>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Make that info easy to find</h2>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Action:</h3>
            <p className="text-gray-300">
              Once everyone has recorded their preferences in miro…
            </p>

            <ol className="space-y-4 list-decimal list-inside">
              <li className="text-gray-300">Thank everyone for contributing. 🎉</li>
              <li className="text-gray-300">
                Invite folks to share their preferences in slack (create a new channel in slack, perhaps something like #feedback-thanks 
                [in the future this becomes a space to say thank you to folks who provide great feedback])
              </li>
              <li className="text-gray-300">Collect everyone's preferences and add to your team directory (optional)</li>
            </ol>

            <h3 className="text-lg font-semibold text-white mt-6">Next Steps:</h3>
            <p className="text-gray-300">
              Hopefully the slack share has encouraged a bit of discussion and 🧵 threads with some "interesting! I didn't know you liked x and didn't like y"
            </p>

            <p className="text-gray-300 font-semibold mt-4">Your mission:</p>
            <p className="text-gray-300">Get collective agreement that:</p>
            <ul className="space-y-2 mt-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                we will be mindful of each other's preferences moving forwards
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                we commit to asking for and giving each other more feedback
              </li>
            </ul>

            <div className="mt-6 space-y-4">
              <h4 className="text-md font-semibold text-white">Lite Approach:</h4>
              <p className="text-gray-300">
                Simple slack thread/ notion page to collect ideas: "We agreed ↗️ feedback would be a good thing for us as a team, 
                now that we've gotten clear on each others preferences. What shall we do differently to make this a thing?"
              </p>

              <h4 className="text-md font-semibold text-white">Heavier Approach:</h4>
              <p className="text-gray-300">
                Co-facilitate a session with each team where you unpack together:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  what each team member is working on (from a personal development pov)
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  what each others preferences are
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  Commitment to give and receive
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  Commitment to publicly praise folks for giving great feedback (#feedback-thanks)
                </li>
              </ul>
            </div>
          </div>
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
};

export default FeedbackCultureStage2; 